import {useState, useCallback, useMemo} from 'react';
import type {ZodSchema} from 'zod';
import {z} from 'zod';

const baseSchema = z
  .string()
  .min(1, 'This field is required')
  .refine((val) => val.trim().length > 0, {
    message: 'Message cannot be only whitespace',
  });

export interface UseTextareaValidationOptions {
  schema?: ZodSchema<string>;
  disabled?: boolean;
}

export interface UseTextareaValidationReturn {
  errors: string[];
  isValid: boolean;
  validate: (value: string) => boolean;
  clearErrors: () => void;
}

export const useTextareaValidation = ({
  schema,
  disabled = false,
}: UseTextareaValidationOptions = {}): UseTextareaValidationReturn => {
  const [errors, setErrors] = useState<string[]>([]);

  const finalSchema = useMemo(() => {
    if (!schema) {
      return baseSchema;
    }
    return baseSchema.and(schema);
  }, [schema]);

  const validate = useCallback(
    (value: string): boolean => {
      if (disabled) {
        setErrors([]);
        return true;
      }

      const result = finalSchema.safeParse(value);

      if (!result.success) {
        const newErrors = result.error.errors.map((err) => err.message);
        setErrors(newErrors);
        return false;
      }

      setErrors([]);
      return true;
    },
    [finalSchema, disabled]
  );

  const clearErrors = useCallback(() => {
    setErrors([]);
  }, []);

  return {
    errors,
    validate,
    clearErrors,
    isValid: errors.length === 0,
  };
};
